import * as angular from 'angular';
import { find, get, inRange } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { QueryOptions, UserGroup } from '@mode/shared/data-access-algolia';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { SelectOption } from '@mode/shared/ui-angularjs';
import { TableListData } from '@mode/shared/ui-angularjs';
import { TableListSchema } from '@mode/shared/ui-angularjs';
import { TableListAvatarValue, TableListValueType } from '@mode/shared/ui-angularjs';

import { RouterLink } from '@mode/shared/ui-angularjs';
import { SearchClientService } from '@mode/algolia/domain';
import { User } from '@mode/algolia/contract';

enum UserFilter {
  all = 'all',
  individuals = 'individuals',
}

enum GroupFilter {
  groups = 'groups',
}

type CategoryFilter = UserFilter | GroupFilter;
type EmptyResultsText = string;
type QueryIndices = 'userEmail' | 'group';

interface AlgoliaSettings {
  index: QueryIndices;
  filter: string;
}

interface CategoryOption extends SelectOption {
  label: string;
  value: CategoryFilter;
}

const DEFAULT_CATEGORY_OPTIONS: CategoryOption[] = [
  { label: 'All Members', value: UserFilter.all },
  { label: 'Groups', value: GroupFilter.groups },
  { label: 'Individuals', value: UserFilter.individuals },
];

const DEFAULT_PAGE = 0;
const DEFAULT_RESULT_PAGE_SIZE = 19;
type PermissionsQueryOptions = QueryOptions<QueryIndices> & {
  rawResults?: true;
};

type UserRouterLink = RouterLink<{
  orgUsername: string;
  dataSourceToken: string;
  username: string;
}>;

type GroupRouterLink = RouterLink<{
  orgUsername: string;
  dataSourceToken: string;
  groupToken: string;
}>;

interface PermissionUserRecord {
  access: string;
  access_via: string;
  name: string;
  username: string;
  email: string;
  token: string;
  avatar: TableListAvatarValue['data'];
  objectType: 'user';
}

interface PermissionUserGroupRecord {
  access: string;
  name: string;
  token: string;
  avatar: null;
  member_count: string;
  objectType: 'user_group';
}

type PermissionRecord = PermissionUserGroupRecord | PermissionUserRecord;

const entitledUserSchema: TableListSchema = {
  name: 'user',
  fields: [
    {
      name: 'avatar',
      title: 'Member',
      type: TableListValueType.Avatar,
    },
    {
      name: 'name',
      title: null,
      type: TableListValueType.Html,
      tooltip: (cell) => cell.name,
    },
    {
      name: 'email',
      title: 'Email',
      type: TableListValueType.Html,
      tooltip: (cell) => cell.email,
    },
    {
      name: 'access',
      title: 'Access',
      type: TableListValueType.Text,
    },
    {
      name: 'access_via',
      title: 'Access Via',
      type: TableListValueType.Text,
    },
  ],
};

const entitledGroupSchema: TableListSchema = {
  name: 'user_group',
  fields: [
    {
      name: 'name',
      title: 'Group',
      type: TableListValueType.Html,
    },
    {
      name: 'member_count',
      title: 'Members',
      type: TableListValueType.Text,
    },
    {
      name: 'access',
      title: 'Access',
      type: TableListValueType.Text,
    },
  ],
};

export class ConnectionPermissionsBrowserController {
  public orgName: string;
  private datasourceToken: string;
  private datasourceCreatorToken: string;
  private organization: any;
  private query$: BehaviorSubject<string> = new BehaviorSubject(null);
  private pageNum$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_PAGE);
  private currPage$: BehaviorSubject<number> = new BehaviorSubject(null);
  private pageCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private hitsCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private permissionRecords$: BehaviorSubject<TableListData<PermissionRecord>[]> = new BehaviorSubject([]);
  private permissionRouterLinks$: Observable<Array<GroupRouterLink | UserRouterLink>>;
  private permissionRecordsTableListSchema$: Observable<TableListSchema>;
  private categoryOptions: CategoryOption[] = DEFAULT_CATEGORY_OPTIONS;
  private selectedCategoryFilter$: BehaviorSubject<CategoryFilter> = new BehaviorSubject(UserFilter.all);
  private selectedCategoryOption$: Observable<CategoryOption>;
  private selectedAlgoliaSettings$: Observable<AlgoliaSettings>;
  private emptyResultsText$: Observable<EmptyResultsText>;
  private initialQueryLoaded = false;

  static $inject = ['SearchClientService', 'AccountModel'];

  constructor(private _searchClientService: SearchClientService, private AccountModel) {
    this.permissionRouterLinks$ = this.permissionRecords$.pipe(
      map((records) => records.map(this.createUserOrGroupLink.bind(this)))
    );
    this.selectedCategoryOption$ = this.selectedCategoryFilter$.pipe(
      map((categoryFilter: CategoryFilter) => find(DEFAULT_CATEGORY_OPTIONS, { value: categoryFilter }))
    );
    this.permissionRecordsTableListSchema$ = this.selectedCategoryFilter$.pipe(
      map((categoryFilter: CategoryFilter) => {
        if (categoryFilter === GroupFilter.groups) {
          return entitledGroupSchema;
        } else {
          return entitledUserSchema;
        }
      })
    );
    this.emptyResultsText$ = combineLatest([this.selectedCategoryFilter$, this.query$]).pipe(
      map(([categoryFilter, query]: [CategoryFilter, string]) => {
        if (query) {
          return 'No search results.';
        } else if (categoryFilter === UserFilter.individuals && !query) {
          return 'No individuals have been added to this connection.';
        } else if (categoryFilter === GroupFilter.groups && !query) {
          return 'No groups have been added to this connection.';
        } else {
          return 'No results';
        }
      })
    );
  }

  $onInit() {
    // get the owner organization
    this.getOwner()
      .then((ownerResponse) => {
        this.organization = ownerResponse;
      })
      .then(() => {
        // set up the AlgoliaSettings to be used in the query
        this.selectedAlgoliaSettings$ = this.selectedCategoryFilter$.pipe(
          map((categoryFilter: CategoryFilter): AlgoliaSettings => {
            const indexDictionary = {
              [GroupFilter.groups]: 'group',
              ['DEFAULT']: 'userEmail',
            };

            const filterDictionary = {
              [GroupFilter.groups]: `organization_${this.organization.token}`,
              [UserFilter.all]: `organization_${this.organization.token}_active`,
              [UserFilter.individuals]: `organization_${this.organization.token}_active,-organization_${this.organization.token}_admin`,
              ['DEFAULT']: `organization_${this.organization.token}_active`,
            };

            return {
              index: indexDictionary[categoryFilter] || indexDictionary['DEFAULT'],
              filter: filterDictionary[categoryFilter] || filterDictionary['DEFAULT'],
            };
          })
        );

        // gather all relevant observables, to query permissions on changes
        const queries$ = this.query$.pipe(filter((query) => query != null));

        combineLatest([queries$, this.pageNum$, this.selectedAlgoliaSettings$]).subscribe(
          ([query, pageNum, algoliaSetting]: [string, number, AlgoliaSettings]) => {
            this.queryPermissions(query, pageNum, algoliaSetting);
          }
        );

        // send the first query
        this.updateQuery();
      });
  }

  createUserOrGroupLink(record: TableListData<PermissionRecord>) {
    const groupLink = (orgUsername, dataSourceToken, groupToken): GroupRouterLink => ({
      stateOrName: 'org-settings.connection.permissions.group-detail',
      params: {
        orgUsername,
        dataSourceToken,
        groupToken,
      },
    });
    const userLink = (orgUsername, dataSourceToken, username): UserRouterLink => ({
      stateOrName: 'org-settings.connection.permissions.user-detail',
      params: {
        orgUsername,
        dataSourceToken,
        username,
      },
    });

    switch (record.values.objectType) {
      case 'user':
        return userLink(this.orgName, this.datasourceToken, record.values.username);
      case 'user_group':
        return groupLink(this.orgName, this.datasourceToken, record.values.token);
      default:
        return null;
    }
  }

  getOwner() {
    return this.AccountModel.find({
      username: this.orgName,
      useEntryPath: true,
      includes: [],
      refresh: true,
    }).catch((err) => {
      BugsnagHelpers.notifySafely(err);
    });
  }

  queryPermissions(query = '', page = DEFAULT_PAGE, algoliaSettings) {
    const opts: PermissionsQueryOptions = {
      page: page,
      query: query || '',
      indexes: [algoliaSettings.index],
      perPage: DEFAULT_RESULT_PAGE_SIZE,
      rawResults: true,
      filters: algoliaSettings.filter,
      facets: {
        userEmail: `connection_permission: ${this.datasourceToken},token:-${this.datasourceCreatorToken}`,
        group: `connection_permission: ${this.datasourceToken},groupType:-everyone`,
      },
    };

    // clear cache if this is the initial query
    if (!this.initialQueryLoaded) {
      opts.clearCache = true;
    }

    const queryPromise = this._searchClientService.query(opts);

    return queryPromise
      .then((response) => {
        if (!this.initialQueryLoaded) {
          this.initialQueryLoaded = true;
        }
        return this.handlePermissionsSearchResponse(response);
      })
      .catch((err) => BugsnagHelpers.notifySafely(err));
  }

  handlePermissionsSearchResponse(response) {
    const currPage = response.results[0].page;
    const pageCount = response.results[0].nbPages;
    const hitsCount = response.results[0].nbHits;
    const hits = response.results[0].hits;

    this.permissionRecords$.next(
      hits.map((hit) => ({
        values: this.createUserOrUserGroupPermissionRecordFromHit(hit),
      }))
    );
    this.currPage$.next(currPage);
    this.pageCount$.next(pageCount);
    this.hitsCount$.next(hitsCount);
  }

  private createUserOrUserGroupPermissionRecordFromHit(hit: User | UserGroup): PermissionRecord {
    switch (hit.objectType) {
      case 'user':
        return this.createUserPermissionRecordFromHit(hit);
      case 'user_group':
        return this.createUserGroupPermissionRecordFromHit(hit);
      default:
        return null;
    }
  }

  private createUserPermissionRecordFromHit(hit: User): PermissionUserRecord {
    const highlight = hit._highlightResult;
    let name = hit.name;
    let email = hit.email;

    if (hit._highlightResult) {
      if (highlight.name) {
        name = highlight.name.value;
      }

      if (highlight.email) {
        email = highlight.email.value;
      }
    }

    return {
      access: get(hit, `connection_access[${this.datasourceToken}].overall_access`, ''),
      access_via: get(hit, `connection_access[${this.datasourceToken}].access_via`, []).join(', '),
      name,
      objectType: hit.objectType,
      username: hit.username,
      token: hit.token,
      email,
      avatar: {
        avatar: hit.avatar,
        size: 30,
      },
    };
  }

  private createUserGroupPermissionRecordFromHit(hit: UserGroup): PermissionUserGroupRecord {
    let name = hit.name;
    const highlight = hit._highlightResult;

    if (hit._highlightResult && highlight.name) {
      name = highlight.name.value;
    }

    return {
      access: get(hit, `connection_access.${this.datasourceToken}`, ''),
      name,
      member_count: `${hit.memberCount} members`,
      objectType: hit.objectType,
      token: hit.token,
      avatar: null,
    };
  }

  //
  // Search & Filtering Logic
  handleSelectFieldChange($option): void {
    this.resetPagination();
    this.updateCategoryFilter($option.value);
  }

  updateCategoryFilter(filterValue): void {
    this.selectedCategoryFilter$.next(filterValue);
  }

  handleQueryUpdate($query: string): void {
    this.updateQuery($query);
  }

  updateQuery(query = '') {
    this.resetPagination();
    this.query$.next(query);
  }

  //
  // Pagination Logic
  updatePage(pageNum: number) {
    this.pageNum$.next(pageNum);
  }

  validPage(page: number, pageRange: number): boolean {
    return inRange(page, 0, pageRange + 1);
  }

  handlePageSelection($page: number) {
    if (this.validPage($page, this.pageCount$.getValue())) {
      this.updatePage($page);
    }
  }

  resetPagination(): void {
    this.currPage$.next(0);
    this.pageNum$.next(0);
  }
}

export const ConnectionPermissionsBrowserComponent: angular.IComponentOptions = {
  controller: ConnectionPermissionsBrowserController,
  controllerAs: 'ctrl',
  bindings: {
    datasource: '<',
    datasourceToken: '@',
    datasourceCreatorToken: '@',
    orgName: '@',
  },
  template: `
    <browser>
      <browser-header>
        <div class="header-content">
          <capra-search-input
            placeholder="Search..."
            (text-change)="ctrl.handleQueryUpdate($event)"
          ></capra-search-input>

          <div class="button-controls">
            <select-field
              options="ctrl.categoryOptions"
              selected="(ctrl.selectedCategoryOption$ | async:this)"
              on-change="ctrl.handleSelectFieldChange($option)"
              label-prefix="View"
            ></select-field>
            <capra-button
              id="single-button"
              type="button"
              class="action-button add-member-btn"
              connection-permissions-add-entitlements-modal
              datasource="ctrl.datasource"
              org-token={{ctrl.organization.token}}
              org-name={{ctrl.orgName}}
              datasource-token={{ctrl.datasourceToken}}
              size="small"
              text="Add members"
            >
            </capra-button>
          </div>
        </div>
      </browser-header>

      <browser-body>
        <h3 class="additional-access-title">Additional Access</h3>
        <table-list
          ng-if="(ctrl.permissionRecords$ | async:this).length "
          router-links="(ctrl.permissionRouterLinks$ | async:this)"
          schema="ctrl.permissionRecordsTableListSchema$ | async:this"
          data="ctrl.permissionRecords$ | async:this"
        />
        <div ng-if="!(ctrl.permissionRecords$ | async:this).length && ctrl.initialQueryLoaded">
          {{ctrl.emptyResultsText$ | async:this}}
        </div>

        <div class="indicator-msg" ng-if="!ctrl.initialQueryLoaded">
          Loading...
        </div>
      </browser-body>

      <browser-footer>
        <paginator
          curr-page="ctrl.currPage$ | async:this"
          page-range="ctrl.pageCount$ | async:this"
          inner-window-size="2"
          left-outer-window-size="1"
          right-outer-window-size="1"
          on-page-selection="ctrl.handlePageSelection($pageIndex)"
          num-items-page="(ctrl.permissionRecords$ | async:this).length"
          num-items-total="ctrl.hitsCount$ | async:this"
        />
      </browser-footer>
    </browser>
  `,
};
